import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp, faFileWord, faTimes } from '@fortawesome/pro-regular-svg-icons';

export const UploadSection = ({
  title = 'Upload File',
  helperText = 'Drag and drop file to upload revision',
  isUploadAllowed = true,
  onUpload,
}) => {
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef();

  const resetFile = () => {
    setFile(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const validateFile = (candidate) => {
    if (!candidate) {
      setError('No file selected.');
      return false;
    }
    const name = candidate.name.toLowerCase();
    const isDoc = name.endsWith('.doc') || name.endsWith('.docx');
    if (!isDoc) {
      setError('Please select a .doc or .docx file.');
      return false;
    }
    if (candidate.size === 0) {
      setError('The file is empty.');
      return false;
    }
    setError('');
    return true;
  };

  const handleFileSelect = (e) => {
    const picked = e.target.files?.[0] ?? null;
    if (validateFile(picked)) setFile(picked);
    else resetFile();
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files?.[0] ?? null;
    if (validateFile(dropped)) setFile(dropped);
    else resetFile();
  };

  const handleFileUpload = async () => {
    if (!validateFile(file)) return;
    setIsUploading(true);
    try {
      if (onUpload) await onUpload(file);
    } finally {
      setIsUploading(false);
      resetFile();
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <CardContent
        className="wrapper"
        onDragOver={isUploadAllowed ? onDragOver : undefined}
        onDragLeave={isUploadAllowed ? onDragLeave : undefined}
        onDrop={isUploadAllowed ? onDrop : undefined}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <input
          ref={fileInputRef}
          accept=".doc,.docx"
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        <IconButton
          className="uploadIcon"
          disabled={isUploading || !isUploadAllowed}
          onClick={() => fileInputRef?.current.click()}
          sx={{ mb: 1 }}
        >
          <FontAwesomeIcon icon={faCloudArrowUp} size="xl" />
        </IconButton>
        <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
        <Typography color="text.secondary">{helperText}</Typography>

        {file && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              width: '100%',
              maxWidth: 420,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              mt: 1,
            }}
          >
            <FontAwesomeIcon icon={faFileWord} />
            <Typography variant="body2" sx={{ flexGrow: 1, wordBreak: 'break-all' }}>
              {file.name}
            </Typography>
            <IconButton size="small" onClick={resetFile} disabled={isUploading}>
              {isUploading ? <CircularProgress size={18} /> : <FontAwesomeIcon icon={faTimes} />}
            </IconButton>
          </Box>
        )}

        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}

        <Button
          className="myOdu__button primary"
          variant="contained"
          disabled={!file || isUploading || !isUploadAllowed}
          onClick={handleFileUpload}
          sx={{ mt: 2 }}
        >
          {isUploading ? 'Uploading...' : 'Submit File'}
        </Button>
      </CardContent>
    </Card>
  );
};
